import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

interface ResizeObserverContextProps {
  observe: (element: Element) => void;
  unobserve: (element: Element) => void;
  size: { width: number; height: number } | null;
}

const ResizeObserverContext = createContext<
  ResizeObserverContextProps | undefined
>(undefined);

export const ResizeObserverProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [size, setSize] = useState<{ width: number; height: number } | null>(
    null
  );
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    resizeObserverRef.current = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const { width, height } = entry.contentRect;
        setSize((prevSize) => {
          if (prevSize?.width !== width || prevSize?.height !== height) {
            return { width, height };
          }
          return prevSize;
        });
      }
    });
    return () => {
      resizeObserverRef.current?.disconnect();
    };
  }, []);

  const observe = (element: Element) => {
    resizeObserverRef.current?.observe(element);
  };

  const unobserve = (element: Element) => {
    resizeObserverRef.current?.unobserve(element);
  };

  return (
    <ResizeObserverContext.Provider value={{ observe, unobserve, size }}>
      {children}
    </ResizeObserverContext.Provider>
  );
};

export const useResizeObserver = () => {
  const context = useContext(ResizeObserverContext);
  if (context === undefined) {
    throw new Error(
      'useResizeObserver must be used within a ResizeObserverProvider'
    );
  }
  return context;
};
